import ScrollerButton from '@components/Scroller/ScrollerButton';
import {LEFT, RIGHT} from '@constants/direction';
import type {ScrollerProps} from '@interfaces/Scroller';
import {getRefElement} from '@utils/refUtils';
import isEqual from 'lodash.isequal';
import throttle from 'lodash.throttle';
import type {ReactElement, ReactNode} from 'react';
import {Children, cloneElement, Component, createRef} from 'react';

export const ERROR_BOUNDARY_MULTIPLIER = 0.01;
export const THROTTLE_INTERVAL = 100;

interface State {
    scrollPosition: {
        [LEFT]: number;
        [RIGHT]: number;
    };
}

export default class Scroller extends Component<ScrollerProps, State> {
    public static defaultProps = {
        scrollButtonShownOffset: 100,
        className: '',
    };

    public readonly state: State = {
        scrollPosition: {
            [LEFT]: 0,
            [RIGHT]: 0,
        },
    };

    private readonly containerRef = this.props.containerRef ?? createRef<HTMLDivElement>();

    private readonly handleScroll = throttle(() => {
        try {
            const container = getRefElement(this.containerRef);
            const scrollPosition = {
                [LEFT]: Scroller.getValueLeft(container),
                [RIGHT]: Scroller.getValueRight(container),
            };
            this.setState({scrollPosition});

            if (this.props.onScrollPosition) {
                this.props.onScrollPosition(scrollPosition[LEFT], scrollPosition[RIGHT]);
            }
        } catch (refError) {
            console.error(refError);
        }
    }, THROTTLE_INTERVAL);

    private static getValueLeft(containerElement: HTMLElement): number {
        return containerElement.scrollLeft;
    }

    private static getValueRight(containerElement: HTMLElement): number {
        return (
            containerElement.scrollWidth -
            containerElement.scrollLeft -
            containerElement.offsetWidth
        );
    }

    private readonly handleButtonClick = (direction: string) => {
        try {
            const container = getRefElement(this.containerRef);

            /*
             * current position need to be incremented or decremented to prevent possible division without remainder
             * because current note can not be shifted in this case
             */
            const delta = this.props.itemWidth * ERROR_BOUNDARY_MULTIPLIER;
            const scrollToValue =
                direction === RIGHT
                    ? Math.ceil((container.scrollLeft + delta) / this.props.itemWidth) *
                      this.props.itemWidth
                    : Math.floor((container.scrollLeft - delta) / this.props.itemWidth) *
                      this.props.itemWidth;

            container.scrollTo({
                left: scrollToValue,
                behavior: 'smooth',
            });
        } catch (error) {
            console.error(error);
        }
    };

    public componentDidMount() {
        try {
            const container = getRefElement(this.containerRef);
            container.addEventListener('scroll', this.handleScroll);
            /*
             * we need to call it manually the first time, otherwise scroller buttons will remain hidden until
             * a user scrolls (which will be hard without scroller buttons)
             */
            this.handleScroll();
        } catch (refError) {
            console.error(refError);
        }
    }

    public componentWillUnmount() {
        try {
            const container = getRefElement(this.containerRef);
            container.removeEventListener('scroll', this.handleScroll);
        } catch (refError) {
            console.error(refError);
        }
    }

    public componentDidUpdate(prevProps: Readonly<ScrollerProps>) {
        if (!isEqual(prevProps.children, this.props.children)) {
            // scroll elements were loaded dynamically, so at this point we need to recalculate positions
            this.handleScroll();
        }
    }

    private isInitialDataLoaded(): boolean {
        return this.props.itemWidth > 0;
    }

    private isButtonShown(position: number): boolean {
        return this.isInitialDataLoaded() && position >= this.props.scrollButtonShownOffset;
    }

    private getChildren() {
        const childrenArray = Children.toArray(this.props.children);
        if (childrenArray.length) {
            return childrenArray.map((item: ReactElement) =>
                item.type === 'img' ? cloneElement(item, {onLoad: this.handleScroll}) : item,
            );
        }
        return this.props.noContentFallback;
    }

    public render(): ReactNode {
        return (
            <div className={`scroller ${this.props.className}`}>
                <ScrollerButton
                    direction={LEFT}
                    isShown={this.isButtonShown(this.state.scrollPosition[LEFT])}
                    {...this.props.buttonProps}
                    {...this.props.leftButtonProps}
                    onClick={this.handleButtonClick}
                />
                <div className="scroller__container" dir="ltr" ref={this.containerRef}>
                    {this.getChildren()}
                </div>
                <ScrollerButton
                    direction={RIGHT}
                    isShown={this.isButtonShown(this.state.scrollPosition[RIGHT])}
                    {...this.props.buttonProps}
                    {...this.props.rightButtonProps}
                    onClick={this.handleButtonClick}
                />
            </div>
        );
    }
}
